import React, { useCallback, useEffect, useState } from 'react';
import {
  MdChevronLeft,
  MdExpandMore,
  MdPlayArrow,
  MdRocketLaunch,
  MdStop,
  MdSync,
  MdUpload,
} from 'react-icons/md';
import { IconButton, Button } from '@aibuild/ui';
import type { ProcessNode, ProcessEdge } from '../graphTypes';
import ProcessActionsMenu from '../../components/ProcessActionsMenu';
import CanvasInfoCard from './CanvasInfoCard';
import CrumbProcessMenu, { type ProcessSummary } from './CrumbProcessMenu';
import ExecutionLog, {
  type ExecutionStep,
  type RunOutcome,
  type RunPhase,
} from './ExecutionLog';
import NodeDetailPanel from './NodeDetailPanel';
import { isMobileViewport } from '@utils/viewport';
import type { TranscriptEntry } from '../runSession/types';
import RunHistoryAnalytics, {
  type RunHistoryStats,
} from './RunHistoryAnalytics';
import {
  BreadcrumbWrap,
  CollapsedRail,
  CrumbAnchor,
  CrumbCurrentButton,
  CrumbMuted,
  CrumbSep,
  DockRoot,
  ExpandButton,
  PanelAside,
  PanelBody,
  PanelFooter,
  PanelHeader,
} from './ProcessSidePanel.styled';

const SidebarIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M15 4v16" />
  </svg>
);

const formatCrumbName = (full: string): string => {
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return full;
  const first = parts[0];
  const lastInitial = (parts.at(-1) ?? '')[0] ?? '';
  return `${first} ${lastInitial.toUpperCase()}.`;
};

interface RunButtonShape {
  readonly leadingIcon: React.ReactNode;
  readonly ariaLabel: string;
  readonly label: string;
}

/**
 * Resolve the published-process Run button's icon, aria-label, and visible
 * text from the two flags that drive it. Pulled out as a flat lookup so the
 * footer JSX doesn't carry three parallel nested-ternary trees.
 *
 * <p>The {@code isRunning} branch is a fallback for when the parent did
 * not wire {@code onStopRun} — when it is wired, {@link PublishedFooter}
 * substitutes a Stop button before consulting this lookup.</p>
 */
const getRunButtonShape = (
  isRunning: boolean,
  hasInput: boolean,
): RunButtonShape => {
  if (isRunning) {
    return {
      leadingIcon: <MdSync />,
      ariaLabel: 'Run in progress',
      label: 'Running…',
    };
  }
  if (hasInput) {
    return {
      leadingIcon: <MdUpload />,
      ariaLabel: 'Upload an input file',
      label: 'Upload input',
    };
  }
  return {
    leadingIcon: <MdPlayArrow />,
    ariaLabel: 'Start a new run',
    label: 'Run',
  };
};

interface PublishedFooterProps {
  isRunning: boolean;
  hasInput: boolean;
  runDisabled?: boolean;
  onRun?: () => void;
  /**
   * Cancel the current published run. Required to render the Stop
   * affordance while {@code isRunning} is true; without it the button
   * falls back to the disabled "Running" placeholder so the user always
   * has visible feedback while a run is in flight even if the parent
   * forgot to wire cancel.
   */
  onStopRun?: () => void;
  /**
   * True while a stop request is already in flight. Disables the Stop
   * button so a second click can't double-fire the cancel mutation.
   */
  isCancelling?: boolean;
  dataTestId: string;
}

/**
 * Footer rendered for published processes — Run / Upload-input button when
 * idle, Stop button while a run is in flight (mirrors the Draft footer's
 * Test Run / Stop toggle so both surfaces feel symmetric).
 */
const PublishedFooter: React.FC<PublishedFooterProps> = ({
  isRunning,
  hasInput,
  runDisabled = false,
  onRun,
  onStopRun,
  isCancelling = false,
  dataTestId,
}) => {
  if (isRunning && onStopRun) {
    return (
      <Button
        variant="filled"
        size="md"
        leadingIcon={<MdStop />}
        onClick={onStopRun}
        disabled={isCancelling}
        aria-label="Stop this run"
        dataTestId={`${dataTestId}__stop-btn`}
      >
        Stop
      </Button>
    );
  }
  const shape = getRunButtonShape(isRunning, hasInput);
  return (
    <Button
      variant="filled"
      size="md"
      leadingIcon={shape.leadingIcon}
      loading={isRunning}
      onClick={onRun}
      disabled={isRunning || !onRun || runDisabled}
      aria-label={shape.ariaLabel}
      dataTestId={`${dataTestId}__run-btn`}
    >
      {shape.label}
    </Button>
  );
};

interface DraftFooterProps {
  isPublishing: boolean;
  testing: boolean;
  runDisabled?: boolean;
  onPublish?: () => void;
  onTestRun?: () => void;
  onStopTestRun?: () => void;
  dataTestId: string;
}

/** Footer rendered for draft processes — Test Run + Publish buttons. */
const DraftFooter: React.FC<DraftFooterProps> = ({
  isPublishing,
  testing,
  runDisabled = false,
  onPublish,
  onTestRun,
  onStopTestRun,
  dataTestId,
}) => (
  <>
    <Button
      variant="outlined"
      size="md"
      leadingIcon={testing ? <MdStop /> : <MdPlayArrow />}
      onClick={testing ? onStopTestRun : onTestRun}
      disabled={!testing && runDisabled}
      aria-label={testing ? 'Stop this test run' : 'Run a test'}
      dataTestId={`${dataTestId}__test-btn`}
    >
      {testing ? 'Stop' : 'Test Run'}
    </Button>
    <Button
      variant="filled"
      size="md"
      leadingIcon={<MdRocketLaunch />}
      loading={isPublishing}
      onClick={onPublish}
      disabled={!onPublish || isPublishing}
      dataTestId={`${dataTestId}__publish-btn`}
    >
      {isPublishing ? 'Publishing…' : 'Publish'}
    </Button>
  </>
);

export interface ProcessSidePanelProps {
  processName: string;
  processId: string;
  isPublished: boolean;
  nodes: ProcessNode[];
  edges: ProcessEdge[];
  ownerName?: string;
  processes?: ProcessSummary[];
  onPublish?: () => void;
  isPublishing?: boolean;
  onRun?: () => void;
  /**
   * Cancel an in-flight published run from the dock footer. Mirrors the
   * draft {@code onStopTestRun} prop — wired by {@code ProcessEditorPage}
   * to {@code session.cancelActive()} for both flows.
   */
  onStopRun?: () => void;
  /**
   * True while the cancel mutation is already in flight (covers both
   * Stop on a Draft test run and Stop on a Published run); disables the
   * Stop button so a second click can't double-fire the cancel.
   */
  isCancelling?: boolean;
  isRunning?: boolean;
  hasInput?: boolean;
  onNavigateToProcess?: (id: string) => void;
  onNewProcess?: () => void;
  onTestRun?: () => void;
  onStopTestRun?: () => void;
  /** Disable run/test-run buttons when the graph has validation errors. */
  runDisabled?: boolean;
  testRunPhase?: RunPhase;
  testRunTotal?: number;
  testRunOrder?: ExecutionStep[];
  testRunCurrentId?: string | null;
  testRunStartedAt?: number | null;
  /**
   * When the most recent test run finished. Pass this for `testRunPhase ===
   * 'done'` so the ExecutionLog's elapsed row freezes at the actual run
   * duration instead of ticking forward as wall-clock time advances.
   */
  testRunFinishedAt?: number | null;
  runNumber?: number;
  /**
   * Whether the run is a test run. Forwarded to {@link ExecutionLog}
   * so it can switch the title between "Test run" (draft / test) and
   * "Run #N" (published, non-test).
   */
  isTest?: boolean;
  runOutcome?: RunOutcome;
  startedBy?: string;
  runnerLabel?: string;
  runHistoryStats?: RunHistoryStats | null;
  /**
   * Header kebab actions. Each is optional — pass only the ones the
   * parent has flows for, and the menu hides the rest. The menu always
   * renders (the kebab itself is unconditional) so the affordance is
   * visible even on read-only / not-owned processes.
   */
  onShowDetails?: () => void;
  onRenameProcess?: () => void;
  onDuplicateProcess?: () => void;
  onSendCopy?: () => void;
  onDeleteProcess?: () => void;
  /**
   * Disable the Delete row (still rendered) with this tooltip — used to
   * explain "published / not owner" cases instead of silently hiding.
   */
  deleteDisabled?: boolean;
  deleteTooltip?: string;
  /**
   * True while a canvas node is selected. The dock body then shows ONLY
   * that node's Agent Log; deselecting (click on empty canvas) brings
   * the summary cards back.
   */
  nodeSelected?: boolean;
  /** Node detail transcript for the currently selected node. */
  selectedNodeTranscript?: readonly TranscriptEntry[];
  selectedNodeError?: string | null;
  selectedNodeErrorDetail?: string | null;
  selectedNodeIsLive?: boolean;
  dataTestId?: string;
}

const COLLAPSED_STORAGE_KEY = 'proc-dock-collapsed';

const ProcessSidePanel: React.FC<ProcessSidePanelProps> = ({
  processName,
  processId,
  isPublished,
  nodes,
  edges,
  ownerName = '',
  processes = [],
  onPublish,
  isPublishing = false,
  onRun,
  onStopRun,
  isCancelling = false,
  isRunning = false,
  hasInput = false,
  onNavigateToProcess,
  onNewProcess,
  onTestRun,
  onStopTestRun,
  runDisabled = false,
  testRunPhase = 'idle',
  testRunTotal = 0,
  testRunOrder = [],
  testRunCurrentId = null,
  testRunStartedAt = null,
  testRunFinishedAt = null,
  runNumber,
  isTest,
  runOutcome = 'completed',
  startedBy,
  runnerLabel,
  runHistoryStats = null,
  onShowDetails,
  onRenameProcess,
  onDuplicateProcess,
  onSendCopy,
  onDeleteProcess,
  deleteDisabled = false,
  deleteTooltip,
  nodeSelected = false,
  selectedNodeTranscript,
  selectedNodeError,
  selectedNodeErrorDetail,
  selectedNodeIsLive = false,
  dataTestId = 'process-side-panel',
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = sessionStorage.getItem(COLLAPSED_STORAGE_KEY);
      return stored === null ? isMobileViewport() : stored === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* no-op */
    }
  }, [collapsed]);

  const [crumbOpen, setCrumbOpen] = useState(false);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);
  const expand = useCallback(() => setCollapsed(false), []);

  const handlePick = useCallback(
    (id: string) => {
      onNavigateToProcess?.(id);
    },
    [onNavigateToProcess],
  );

  const handleNew = useCallback(() => {
    onNewProcess?.();
  }, [onNewProcess]);

  const testing = testRunPhase === 'running';

  return (
    <DockRoot $collapsed={collapsed} data-testid={dataTestId}>
      {/* Collapsed rail */}
      <CollapsedRail
        $visible={collapsed}
        aria-label="Process panel (collapsed)"
        aria-hidden={!collapsed}
      >
        <ExpandButton
          onClick={expand}
          title="Expand panel"
          aria-label="Expand panel"
          data-testid={`${dataTestId}__expand`}
        >
          <MdChevronLeft size={18} />
        </ExpandButton>
      </CollapsedRail>

      {/* Expanded panel */}
      <PanelAside
        $visible={!collapsed}
        aria-label="Process panel"
        aria-hidden={collapsed}
      >
        <PanelHeader>
          <IconButton
            ariaLabel={collapsed ? 'Expand panel' : 'Collapse panel'}
            icon={<SidebarIcon />}
            size="md"
            variant="ghost"
            onClick={toggleCollapsed}
            dataTestId={`${dataTestId}__collapse-toggle`}
          />

          <BreadcrumbWrap>
            {ownerName && (
              <>
                <CrumbMuted>{formatCrumbName(ownerName)}</CrumbMuted>
                <CrumbSep>/</CrumbSep>
              </>
            )}
            <CrumbAnchor>
              <CrumbCurrentButton
                type="button"
                title={processName}
                aria-haspopup="menu"
                aria-expanded={crumbOpen}
                onClick={() => setCrumbOpen((v) => !v)}
                onMouseDown={(e) => {
                  if (crumbOpen) e.stopPropagation();
                }}
                $open={crumbOpen}
                data-testid={`${dataTestId}__crumb-current`}
              >
                {processName}
                <MdExpandMore />
              </CrumbCurrentButton>
              <CrumbProcessMenu
                open={crumbOpen}
                onClose={() => setCrumbOpen(false)}
                currentId={processId}
                processes={processes}
                onPick={handlePick}
                onNew={handleNew}
              />
            </CrumbAnchor>
          </BreadcrumbWrap>

          <ProcessActionsMenu
            ariaLabel={`Actions for ${processName}`}
            triggerSize="md"
            onDetails={onShowDetails}
            onRename={onRenameProcess}
            onDuplicate={onDuplicateProcess}
            onSendCopy={onSendCopy}
            onDelete={onDeleteProcess}
            deleteDisabled={deleteDisabled}
            deleteTooltip={deleteTooltip}
            dataTestId={`${dataTestId}__options`}
          />
        </PanelHeader>

        <PanelBody>
          {nodeSelected ? (
            <NodeDetailPanel
              errorMessage={selectedNodeError ?? null}
              errorDetail={selectedNodeErrorDetail ?? null}
              transcript={selectedNodeTranscript ?? []}
              isLive={selectedNodeIsLive}
            />
          ) : (
            <>
              <CanvasInfoCard nodes={nodes} edges={edges} />
              <RunHistoryAnalytics stats={runHistoryStats} />
              <ExecutionLog
                phase={testRunPhase}
                total={testRunTotal}
                order={testRunOrder}
                currentId={testRunCurrentId}
                startedAt={testRunStartedAt}
                finishedAt={testRunFinishedAt}
                runNumber={runNumber}
                isTest={isTest}
                runOutcome={runOutcome}
                startedBy={startedBy}
                runnerLabel={runnerLabel}
              />
            </>
          )}
        </PanelBody>

        <PanelFooter>
          {isPublished ? (
            <PublishedFooter
              isRunning={isRunning}
              hasInput={hasInput}
              runDisabled={runDisabled}
              onRun={onRun}
              onStopRun={onStopRun}
              isCancelling={isCancelling}
              dataTestId={dataTestId}
            />
          ) : (
            <DraftFooter
              isPublishing={isPublishing}
              testing={testing}
              runDisabled={runDisabled}
              onPublish={onPublish}
              onTestRun={onTestRun}
              onStopTestRun={onStopTestRun}
              dataTestId={dataTestId}
            />
          )}
        </PanelFooter>
      </PanelAside>
    </DockRoot>
  );
};

/**
 * Memoised so a drag tick (parent re-renders with identity-stable
 * props) skips the panel entirely.
 */
export default React.memo(ProcessSidePanel);
